import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, filter, map, withLatestFrom} from 'rxjs/operators';
import {CourseCouponsDbService} from '../services/course-coupons-db.service';
import {select, Store} from '@ngrx/store';
import {selectActiveCourse} from './selectors';
import {AppState} from './index';
import {CourseCoupon} from '../models/coupon.model';
import {EMPTY, throwError} from 'rxjs';
import {CouponActions} from './action-types';
import {addCoupons, loadCouponCompleted, deleteCouponSuccess} from './coupons.actions';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';

@Injectable({
  providedIn: 'root'
})
export class CouponEffects {
  private actions$ = inject(Actions);
  private dbCoupons = inject(CourseCouponsDbService);
  private store = inject<Store<AppState>>(Store);
  private messages = inject(MessagesService);
  private loading = inject(LoadingService);


  loadCoupons$ = createEffect(() => this.actions$
    .pipe(
      ofType(CouponActions.loadCoupons),
      withLatestFrom(this.store.pipe(select(selectActiveCourse))),
      filter(([, course]) => !!course),
      concatMap(([{activeCouponsOnly}, course]) => this.loading.showLoader<CourseCoupon[]>(
        this.dbCoupons.loadCoupons(course.id, activeCouponsOnly)).pipe(
          map(coupons => addCoupons({coupons})),
          catchError(_err => {
            this.messages.error('Could not load course coupons.');
            return EMPTY;
          })
        )
      )
    ));


  loadCoupon$ = createEffect(() => this.actions$
    .pipe(
      ofType(CouponActions.loadCoupon),
      concatMap(({courseId, couponCode}) => this.loading.showLoader(this.dbCoupons.findCouponByCode(courseId, couponCode))),
      map(coupon => loadCouponCompleted({coupon}))
    ));


  saveCoupon$ = createEffect(() => this.actions$
    .pipe(
      ofType(CouponActions.updateCoupon),
      concatMap(({courseId,coupon}) => this.dbCoupons.saveCoupon(courseId, coupon.id as string, coupon.changes))
    ),
    {dispatch: false});


  createCoupon$ = createEffect(() => this.actions$
    .pipe(
      ofType(CouponActions.addCoupon),
      concatMap(({coupon}) => this.dbCoupons.createNewCoupon(coupon.courseId, coupon))
    ),
    {dispatch: false});

  deleteCoupon$ = createEffect(() => this.actions$
    .pipe(
      ofType(CouponActions.deleteCoupon),
      concatMap(({courseId, couponId}) => 
        this.dbCoupons.deleteCoupon(courseId, couponId).pipe(
          map(() => deleteCouponSuccess({couponId}))
        )
      ),
      catchError(err => {
        this.messages.error('Could not delete coupon.');
        return throwError(() => err);
      })
    ));

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

}
